import React, {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";

import {
    FiArrowLeft,
    FiBookOpen,
    FiCreditCard,
    FiFileText,
    FiLoader,
    FiRefreshCw,
    FiSearch,
    FiTrendingDown,
    FiTrendingUp,
    FiUser,
} from "react-icons/fi";

import {
    useDispatch,
    useSelector,
} from "react-redux";

import {
    useHistory,
    useParams,
} from "react-router-dom";

import {
    clearSelectedSupplierLedger,
    fetchSupplierLedgerBySupplier,
    recalculateSupplierLedger,
    selectedSupplierLedgerSelector,
    supplierLedgerErrorSelector,
    supplierLedgerLoadingSelector,
} from "../../../../../../store/billing/supplierLedger/supplierLedgerSlice";

import "./SupplierLedger.css";

/*
|--------------------------------------------------------------------------
| CONSTANTS
|--------------------------------------------------------------------------
*/

const PAGE_SIZE_OPTIONS = [
    10,
    20,
    50,
    100,
];

/*
|--------------------------------------------------------------------------
| HELPERS
|--------------------------------------------------------------------------
*/

const toNumber = (
    value,
    fallback = 0
) => {
    const number =
        Number(value);

    return Number.isFinite(
        number
    )
        ? number
        : fallback;
};

const roundMoney = (
    value
) =>
    Math.round(
        (
            toNumber(value) +
            Number.EPSILON
        ) *
            100
    ) / 100;

const formatCurrency = (
    value
) =>
    new Intl.NumberFormat(
        "en-IN",
        {
            style:
                "currency",

            currency:
                "INR",

            minimumFractionDigits:
                2,

            maximumFractionDigits:
                2,
        }
    ).format(
        roundMoney(value)
    );

const formatDate = (
    value
) => {
    if (!value) {
        return "-";
    }

    const date =
        new Date(value);

    if (
        Number.isNaN(
            date.getTime()
        )
    ) {
        return "-";
    }

    return new Intl.DateTimeFormat(
        "en-IN",
        {
            day:
                "2-digit",

            month:
                "short",

            year:
                "numeric",
        }
    ).format(
        date
    );
};

const formatLabel = (
    value
) =>
    String(
        value ||
            "-"
    )
        .replace(
            /_/g,
            " "
        )
        .toLowerCase()
        .replace(
            /\b\w/g,
            (
                character
            ) =>
                character.toUpperCase()
        );

const getDocumentNumber = (
    entry
) =>
    entry?.documentNumber ||
    entry?.paymentNumber ||
    entry?.debitNoteNumber ||
    "-";

const getTransactionClass = (
    type
) => {
    switch (
        String(
            type ||
                ""
        ).toUpperCase()
    ) {
        case "PURCHASE_BILL":
            return "purchase";

        case "PAYMENT_OUT":
            return "payment";

        case "DEBIT_NOTE":
            return "debit-note";

        default:
            return "default";
    }
};

/*
|--------------------------------------------------------------------------
| COMPONENT
|--------------------------------------------------------------------------
*/

const SupplierLedgerDetailsPage =
    () => {
        const dispatch =
            useDispatch();

        const history =
            useHistory();

        const {
            supplierId,
        } =
            useParams();

        const ledger =
            useSelector(
                selectedSupplierLedgerSelector
            );

        const loading =
            useSelector(
                supplierLedgerLoadingSelector
            ) || {};

        const errors =
            useSelector(
                supplierLedgerErrorSelector
            ) || {};

        const supplier =
            ledger?.supplier ||
            null;

        const balance =
            ledger?.balance ||
            {};

        const records =
            ledger?.records ||
            [];

        const pagination =
            ledger?.pagination ||
            {};

        /*
        |--------------------------------------------------------------------------
        | FILTERS
        |--------------------------------------------------------------------------
        */

        const [
            search,
            setSearch,
        ] = useState("");

        const [
            transactionType,
            setTransactionType,
        ] = useState("");

        const [
            fromDate,
            setFromDate,
        ] = useState("");

        const [
            toDate,
            setToDate,
        ] = useState("");

        const [
            page,
            setPage,
        ] = useState(1);

        const [
            limit,
            setLimit,
        ] = useState(20);

        const [
            localError,
            setLocalError,
        ] = useState("");

        /*
        |--------------------------------------------------------------------------
        | LOAD LEDGER
        |--------------------------------------------------------------------------
        */

        const loadLedger =
            useCallback(
                async () => {
                    if (
                        !supplierId
                    ) {
                        return;
                    }

                    try {
                        setLocalError(
                            ""
                        );

                        await dispatch(
                            fetchSupplierLedgerBySupplier({
                                supplierId,

                                page,

                                limit,

                                search:
                                    search.trim(),

                                transactionType,

                                fromDate,

                                toDate,

                                status:
                                    "ACTIVE",
                            })
                        ).unwrap();
                    } catch (
                        error
                    ) {
                        setLocalError(
                            typeof error ===
                                "string"
                                ? error
                                : "Unable to load supplier ledger."
                        );
                    }
                },
                [
                    dispatch,
                    supplierId,
                    page,
                    limit,
                    search,
                    transactionType,
                    fromDate,
                    toDate,
                ]
            );

        useEffect(
            () => {
                const timer =
                    setTimeout(
                        () => {
                            loadLedger();
                        },
                        300
                    );

                return () =>
                    clearTimeout(
                        timer
                    );
            },
            [
                loadLedger,
            ]
        );

        /*
        |--------------------------------------------------------------------------
        | CLEANUP
        |--------------------------------------------------------------------------
        */

        useEffect(
            () => {
                return () => {
                    dispatch(
                        clearSelectedSupplierLedger()
                    );
                };
            },
            [
                dispatch,
            ]
        );

        /*
        |--------------------------------------------------------------------------
        | SUPPLIER INFO
        |--------------------------------------------------------------------------
        */

        const supplierSnapshot =
            supplier?.snapshot ||
            records?.[0]
                ?.supplierSnapshot ||
            {};

        const supplierName =
            supplierSnapshot
                ?.supplierName ||
            supplierSnapshot
                ?.companyName ||
            "Supplier";

        /*
        |--------------------------------------------------------------------------
        | PAGE TOTALS
        |--------------------------------------------------------------------------
        */

        const pageTotals =
            useMemo(
                () => {
                    return records.reduce(
                        (
                            result,
                            entry
                        ) => {
                            result.debit +=
                                toNumber(
                                    entry
                                        ?.debit
                                );

                            result.credit +=
                                toNumber(
                                    entry
                                        ?.credit
                                );

                            return result;
                        },
                        {
                            debit:
                                0,

                            credit:
                                0,
                        }
                    );
                },
                [
                    records,
                ]
            );

        /*
        |--------------------------------------------------------------------------
        | RECALCULATE
        |--------------------------------------------------------------------------
        */

        const handleRecalculate =
            async () => {
                if (
                    !supplierId
                ) {
                    return;
                }

                const approved =
                    window.confirm(
                        "Recalculate all running balances for this supplier?"
                    );

                if (
                    !approved
                ) {
                    return;
                }

                try {
                    setLocalError(
                        ""
                    );

                    await dispatch(
                        recalculateSupplierLedger(
                            supplierId
                        )
                    ).unwrap();

                    await loadLedger();
                } catch (
                    error
                ) {
                    setLocalError(
                        typeof error ===
                            "string"
                            ? error
                            : "Unable to recalculate supplier ledger."
                    );
                }
            };

        /*
        |--------------------------------------------------------------------------
        | CLEAR FILTERS
        |--------------------------------------------------------------------------
        */

        const handleClearFilters =
            () => {
                setSearch(
                    ""
                );

                setTransactionType(
                    ""
                );

                setFromDate(
                    ""
                );

                setToDate(
                    ""
                );

                setPage(
                    1
                );
            };

        /*
        |--------------------------------------------------------------------------
        | PAGINATION
        |--------------------------------------------------------------------------
        */

        const totalRecords =
            toNumber(
                pagination
                    ?.total
            );

        const totalPages =
            Math.max(
                toNumber(
                    pagination
                        ?.totalPages,
                    1
                ),
                1
            );

        const currentPage =
            Math.max(
                toNumber(
                    pagination
                        ?.page,
                    page
                ),
                1
            );

        const startRecord =
            totalRecords >
            0
                ? (
                      currentPage -
                      1
                  ) *
                      limit +
                  1
                : 0;

        const endRecord =
            Math.min(
                currentPage *
                    limit,
                totalRecords
            );

        /*
        |--------------------------------------------------------------------------
        | RENDER
        |--------------------------------------------------------------------------
        */

        return (
            <div className="supplier-ledger-page">
                {/* HEADER */}

                <header className="supplier-ledger-header">
                    <div className="supplier-ledger-heading">
                        <button
                            type="button"
                            className="supplier-ledger-icon-button"
                            onClick={() =>
                                history.push(
                                    "/billing/supplier-ledger"
                                )
                            }
                        >
                            <FiArrowLeft />
                        </button>

                        <span className="supplier-ledger-heading-icon">
                            <FiBookOpen />
                        </span>

                        <div>
                            <small>
                                SUPPLIER LEDGER
                            </small>

                            <h1>
                                {
                                    supplierName
                                }
                            </h1>

                            <p>
                                Complete purchase and payment transaction history.
                            </p>
                        </div>
                    </div>

                    <div className="supplier-ledger-header-actions">
                        <button
                            type="button"
                            className="supplier-ledger-button"
                            disabled={
                                loading
                                    .supplierDetails
                            }
                            onClick={
                                loadLedger
                            }
                        >
                            <FiRefreshCw
                                className={
                                    loading
                                        .supplierDetails
                                        ? "supplier-ledger-spinner"
                                        : ""
                                }
                            />

                            Refresh
                        </button>

                        <button
                            type="button"
                            className="supplier-ledger-button"
                            disabled={
                                loading
                                    .recalculate
                            }
                            onClick={
                                handleRecalculate
                            }
                        >
                            {loading
                                .recalculate ? (
                                <FiLoader className="supplier-ledger-spinner" />
                            ) : (
                                <FiRefreshCw />
                            )}

                            Recalculate
                        </button>
                    </div>
                </header>

                {/* ERROR */}

                {(localError ||
                    errors
                        .supplierDetails ||
                    errors
                        .recalculate) && (
                    <div className="supplier-ledger-alert">
                        {localError ||
                            errors
                                .supplierDetails ||
                            errors
                                .recalculate}
                    </div>
                )}

                {/* SUPPLIER */}

                <section className="supplier-ledger-supplier-card">
                    <div className="supplier-ledger-avatar">
                        <FiUser />
                    </div>

                    <div className="supplier-ledger-supplier-info">
                        <h2>
                            {
                                supplierName
                            }
                        </h2>

                        <div>
                            {supplierSnapshot
                                ?.mobile && (
                                <span>
                                    Mobile:{" "}
                                    {
                                        supplierSnapshot.mobile
                                    }
                                </span>
                            )}

                            {supplierSnapshot
                                ?.email && (
                                <span>
                                    Email:{" "}
                                    {
                                        supplierSnapshot.email
                                    }
                                </span>
                            )}

                            {supplierSnapshot
                                ?.gstin && (
                                <span>
                                    GSTIN:{" "}
                                    {
                                        supplierSnapshot.gstin
                                    }
                                </span>
                            )}
                        </div>
                    </div>
                </section>

                {/* BALANCE SUMMARY */}

                <section className="supplier-ledger-summary-grid">
                    <div className="supplier-ledger-summary-card">
                        <span>
                            Total Credit
                        </span>

                        <strong>
                            {formatCurrency(
                                balance
                                    ?.totalCredit
                            )}
                        </strong>

                        <small>
                            Payable created
                        </small>
                    </div>

                    <div className="supplier-ledger-summary-card supplier-ledger-summary-payment">
                        <span>
                            Total Debit
                        </span>

                        <strong>
                            {formatCurrency(
                                balance
                                    ?.totalDebit
                            )}
                        </strong>

                        <small>
                            Payments & adjustments
                        </small>
                    </div>

                    <div className="supplier-ledger-summary-card">
                        <span>
                            Transactions
                        </span>

                        <strong>
                            {
                                totalRecords
                            }
                        </strong>

                        <small>
                            Ledger entries
                        </small>
                    </div>

                    <div className="supplier-ledger-summary-card supplier-ledger-summary-balance">
                        <span>
                            Outstanding Payable
                        </span>

                        <strong>
                            {formatCurrency(
                                balance
                                    ?.balance
                            )}
                        </strong>

                        <small>
                            Current supplier balance
                        </small>
                    </div>
                </section>

                {/* FILTERS */}

                <section className="supplier-ledger-toolbar">
                    <div className="supplier-ledger-search">
                        <FiSearch />

                        <input
                            type="text"
                            value={
                                search
                            }
                            placeholder="Search document, payment, debit note, narration..."
                            onChange={(
                                event
                            ) => {
                                setPage(
                                    1
                                );

                                setSearch(
                                    event
                                        .target
                                        .value
                                );
                            }}
                        />
                    </div>

                    <select
                        value={
                            transactionType
                        }
                        onChange={(
                            event
                        ) => {
                            setPage(
                                1
                            );

                            setTransactionType(
                                event
                                    .target
                                    .value
                            );
                        }}
                    >
                        <option value="">
                            All Transactions
                        </option>

                        <option value="PURCHASE_BILL">
                            Purchase Bill
                        </option>

                        <option value="PAYMENT_OUT">
                            Payment Out
                        </option>

                        <option value="DEBIT_NOTE">
                            Debit Note
                        </option>

                        <option value="OPENING_BALANCE">
                            Opening Balance
                        </option>

                        <option value="ADJUSTMENT">
                            Adjustment
                        </option>
                    </select>

                    <input
                        type="date"
                        value={
                            fromDate
                        }
                        onChange={(
                            event
                        ) => {
                            setPage(
                                1
                            );

                            setFromDate(
                                event
                                    .target
                                    .value
                            );
                        }}
                    />

                    <input
                        type="date"
                        value={
                            toDate
                        }
                        min={
                            fromDate ||
                            undefined
                        }
                        onChange={(
                            event
                        ) => {
                            setPage(
                                1
                            );

                            setToDate(
                                event
                                    .target
                                    .value
                            );
                        }}
                    />

                    <button
                        type="button"
                        className="supplier-ledger-button"
                        onClick={
                            handleClearFilters
                        }
                    >
                        Clear
                    </button>
                </section>

                {/* TABLE */}

                <section className="supplier-ledger-card">
                    {loading
                        .supplierDetails &&
                    records.length ===
                        0 ? (
                        <div className="supplier-ledger-empty">
                            <FiLoader className="supplier-ledger-spinner" />

                            <h3>
                                Loading ledger...
                            </h3>
                        </div>
                    ) : records.length ===
                      0 ? (
                        <div className="supplier-ledger-empty">
                            <FiFileText />

                            <h3>
                                No transactions found
                            </h3>

                            <p>
                                No ledger transactions match the selected filters.
                            </p>
                        </div>
                    ) : (
                        <div className="supplier-ledger-table-wrapper">
                            <table className="supplier-ledger-table">
                                <thead>
                                    <tr>
                                        <th>
                                            Date
                                        </th>

                                        <th>
                                            Transaction
                                        </th>

                                        <th>
                                            Document
                                        </th>

                                        <th>
                                            Narration
                                        </th>

                                        <th className="text-right">
                                            Opening
                                        </th>

                                        <th className="text-right">
                                            Debit
                                        </th>

                                        <th className="text-right">
                                            Credit
                                        </th>

                                        <th className="text-right">
                                            Balance
                                        </th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {records.map(
                                        (
                                            entry,
                                            index
                                        ) => (
                                            <tr
                                                key={
                                                    entry
                                                        ?._id ||
                                                    index
                                                }
                                            >
                                                <td>
                                                    {formatDate(
                                                        entry
                                                            ?.transactionDate
                                                    )}
                                                </td>

                                                <td>
                                                    <span
                                                        className={`supplier-ledger-type supplier-ledger-type-${getTransactionClass(
                                                            entry
                                                                ?.transactionType
                                                        )}`}
                                                    >
                                                        {entry
                                                            ?.transactionType ===
                                                        "PURCHASE_BILL" ? (
                                                            <FiFileText />
                                                        ) : entry
                                                              ?.transactionType ===
                                                          "PAYMENT_OUT" ? (
                                                            <FiCreditCard />
                                                        ) : entry
                                                              ?.direction ===
                                                          "DEBIT" ? (
                                                            <FiTrendingDown />
                                                        ) : (
                                                            <FiTrendingUp />
                                                        )}

                                                        {formatLabel(
                                                            entry
                                                                ?.transactionType
                                                        )}
                                                    </span>
                                                </td>

                                                <td>
                                                    <div className="supplier-ledger-document">
                                                        <strong>
                                                            {getDocumentNumber(
                                                                entry
                                                            )}
                                                        </strong>

                                                        {entry
                                                            ?.referenceNumber && (
                                                            <span>
                                                                Ref:{" "}
                                                                {
                                                                    entry.referenceNumber
                                                                }
                                                            </span>
                                                        )}
                                                    </div>
                                                </td>

                                                <td>
                                                    <div className="supplier-ledger-narration">
                                                        {entry
                                                            ?.narration ||
                                                            "-"}
                                                    </div>
                                                </td>

                                                <td className="text-right">
                                                    {formatCurrency(
                                                        entry
                                                            ?.openingBalance
                                                    )}
                                                </td>

                                                <td className="text-right supplier-ledger-debit">
                                                    {toNumber(
                                                        entry
                                                            ?.debit
                                                    ) >
                                                    0
                                                        ? formatCurrency(
                                                              entry
                                                                  ?.debit
                                                          )
                                                        : "-"}
                                                </td>

                                                <td className="text-right supplier-ledger-credit">
                                                    {toNumber(
                                                        entry
                                                            ?.credit
                                                    ) >
                                                    0
                                                        ? formatCurrency(
                                                              entry
                                                                  ?.credit
                                                          )
                                                        : "-"}
                                                </td>

                                                <td className="text-right">
                                                    <strong
                                                        className={
                                                            toNumber(
                                                                entry
                                                                    ?.closingBalance
                                                            ) >
                                                            0
                                                                ? "supplier-ledger-balance-payable"
                                                                : "supplier-ledger-balance-clear"
                                                        }
                                                    >
                                                        {formatCurrency(
                                                            entry
                                                                ?.closingBalance ??
                                                                entry
                                                                    ?.runningBalance
                                                        )}
                                                    </strong>
                                                </td>
                                            </tr>
                                        )
                                    )}
                                </tbody>

                                <tfoot>
                                    <tr>
                                        <td
                                            colSpan="5"
                                            className="text-right"
                                        >
                                            <strong>
                                                Page Total
                                            </strong>
                                        </td>

                                        <td className="text-right supplier-ledger-debit">
                                            <strong>
                                                {formatCurrency(
                                                    pageTotals.debit
                                                )}
                                            </strong>
                                        </td>

                                        <td className="text-right supplier-ledger-credit">
                                            <strong>
                                                {formatCurrency(
                                                    pageTotals.credit
                                                )}
                                            </strong>
                                        </td>

                                        <td />
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    )}

                    {/* PAGINATION */}

                    <footer className="supplier-ledger-pagination">
                        <div>
                            <span>
                                Showing{" "}
                                <strong>
                                    {
                                        startRecord
                                    }
                                </strong>
                                {" - "}
                                <strong>
                                    {
                                        endRecord
                                    }
                                </strong>
                                {" of "}
                                <strong>
                                    {
                                        totalRecords
                                    }
                                </strong>
                            </span>

                            <select
                                value={
                                    limit
                                }
                                onChange={(
                                    event
                                ) => {
                                    setPage(
                                        1
                                    );

                                    setLimit(
                                        Number(
                                            event
                                                .target
                                                .value
                                        )
                                    );
                                }}
                            >
                                {PAGE_SIZE_OPTIONS.map(
                                    (
                                        size
                                    ) => (
                                        <option
                                            key={
                                                size
                                            }
                                            value={
                                                size
                                            }
                                        >
                                            {size}
                                        </option>
                                    )
                                )}
                            </select>
                        </div>

                        <div>
                            <button
                                type="button"
                                disabled={
                                    currentPage <=
                                        1 ||
                                    loading
                                        .supplierDetails
                                }
                                onClick={() =>
                                    setPage(
                                        Math.max(
                                            currentPage -
                                                1,
                                            1
                                        )
                                    )
                                }
                            >
                                Previous
                            </button>

                            <span>
                                Page{" "}
                                <strong>
                                    {
                                        currentPage
                                    }
                                </strong>{" "}
                                of{" "}
                                <strong>
                                    {
                                        totalPages
                                    }
                                </strong>
                            </span>

                            <button
                                type="button"
                                disabled={
                                    currentPage >=
                                        totalPages ||
                                    loading
                                        .supplierDetails
                                }
                                onClick={() =>
                                    setPage(
                                        Math.min(
                                            currentPage +
                                                1,
                                            totalPages
                                        )
                                    )
                                }
                            >
                                Next
                            </button>
                        </div>
                    </footer>
                </section>
            </div>
        );
    };

export default SupplierLedgerDetailsPage;